import type { ButtonHTMLAttributes } from "react";

export type ModalActionMode = "create" | "edit" | "view" | "custom";

interface ModalActionsProps {
  readonly mode: ModalActionMode;
  readonly hasChanges?: boolean;
  readonly isSaving?: boolean;
  readonly isValid?: boolean;
  readonly onCancel: () => void;
  readonly onSave?: () => void;
  readonly onEdit?: () => void;
  readonly cancelLabel?: string;
  readonly saveLabel?: string;
  readonly editLabel?: string;
  readonly customPrimaryAction?: {
    label: string;
    onClick: () => void;
    disabled?: boolean;
    variant?: "primary" | "danger";
  };
}

/**
 * Composant standardisé pour les actions de modal (footer).
 *
 * Modes:
 * - `create`: Annuler + Créer (disabled si !isValid)
 * - `edit`: Annuler + Modifier (disabled si !hasChanges)
 * - `view`: Annuler + Modifier (passe en mode edit)
 * - `custom`: Annuler + action personnalisée
 */
export function ModalActions({
  mode,
  hasChanges = false,
  isSaving = false,
  isValid = true,
  onCancel,
  onSave,
  onEdit,
  cancelLabel = "Annuler",
  saveLabel = "Enregistrer",
  editLabel = "Modifier",
  customPrimaryAction,
}: ModalActionsProps) {
  const getPrimaryButton = () => {
    switch (mode) {
      case "create":
        return (
          <button
            type="button"
            onClick={onSave}
            disabled={!isValid || isSaving}
            className="px-6 py-2.5 text-sm font-medium bg-accent text-white rounded-lg hover:bg-accent/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] touch-feedback"
          >
            {isSaving ? "Enregistrement..." : saveLabel}
          </button>
        );

      case "edit":
        return (
          <button
            type="button"
            onClick={onSave}
            disabled={!hasChanges || !isValid || isSaving}
            className="px-6 py-2.5 text-sm font-medium bg-accent text-white rounded-lg hover:bg-accent/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] touch-feedback"
          >
            {isSaving ? "Enregistrement..." : saveLabel}
          </button>
        );

      case "view":
        return (
          <button
            type="button"
            onClick={onEdit}
            className="px-6 py-2.5 text-sm font-medium bg-accent text-white rounded-lg hover:bg-accent/90 transition-colors min-h-[44px] touch-feedback"
          >
            {editLabel}
          </button>
        );

      case "custom": {
        if (!customPrimaryAction) return null;
        const dangerClass = customPrimaryAction.variant === "danger"
          ? "bg-danger text-white hover:bg-danger/90"
          : "bg-accent text-white hover:bg-accent/90";

        return (
          <button
            type="button"
            onClick={customPrimaryAction.onClick}
            disabled={customPrimaryAction.disabled ?? false}
            className={`px-6 py-2.5 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] touch-feedback ${dangerClass}`}
          >
            {customPrimaryAction.label}
          </button>
        );
      }
    }
  };

  return (
    <div className="flex items-center justify-end gap-3">
      <button
        type="button"
        onClick={onCancel}
        disabled={isSaving}
        className="px-6 py-2.5 text-sm font-medium text-ink-muted hover:text-ink transition-colors disabled:opacity-50 min-h-[44px] min-w-[80px] touch-feedback"
      >
        {cancelLabel}
      </button>
      {getPrimaryButton()}
    </div>
  );
}

/**
 * Props pour un bouton d'action modal (à utiliser avec le footer du Modal)
 */
export interface ModalActionButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly variant?: "primary" | "secondary" | "danger";
  readonly isLoading?: boolean;
}

/**
 * Bouton d'action standardisé pour modals
 */
export function ModalActionButton({
  variant = "primary",
  isLoading = false,
  disabled,
  children,
  className = "",
  ...props
}: ModalActionButtonProps) {
  const baseClasses = "px-6 py-2.5 text-sm font-medium rounded-lg transition-all min-h-[44px] touch-feedback";

  const variantClasses = {
    primary: disabled ? "bg-surface-alt text-ink-faint border border-border/40" : "bg-accent text-white hover:bg-accent/90 shadow-sm",
    secondary: disabled ? "bg-surface text-ink-faint" : "text-ink-muted hover:text-ink hover:bg-surface-alt",
    danger: disabled ? "bg-surface-alt text-ink-faint border border-border/40" : "bg-danger text-white hover:bg-danger/90",
  };

  const disabledClasses = disabled || isLoading ? "opacity-60 cursor-not-allowed" : "";

  return (
    <button
      type="button"
      disabled={disabled || isLoading}
      className={`${baseClasses} ${variantClasses[variant]} ${disabledClasses} ${className}`}
      {...props}
    >
      {isLoading ? "Chargement..." : children}
    </button>
  );
}
